import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface TeamMember {
  id: string;
  user_id: string;
  team_id: string;
  role: "owner" | "admin" | "member";
  joined_at: string;
  profiles?: { name: string | null; email: string } | null;
}

export interface Team {
  id: string;
  name: string;
  owner_id: string;
  seat_count: number;
  subscription_status: string | null;
  subscription_plan: string | null;
  subscription_period_end: string | null;
  billing_cycle: string | null;
  polar_subscription_id: string | null;
}

export interface TeamInvitation {
  id: string;
  email: string;
  role: string;
  status: string;
  created_at: string;
  expires_at: string;
}

export function useTeam() {
  const [team, setTeam] = useState<Team | null>(null);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [invitations, setInvitations] = useState<TeamInvitation[]>([]);
  const [myRole, setMyRole] = useState<"owner" | "admin" | "member" | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchTeam = useCallback(async () => {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) {
      setLoading(false);
      return;
    }

    const { data: membership } = await supabase
      .from("team_members")
      .select("team_id, role")
      .eq("user_id", user.id)
      .maybeSingle();

    if (!membership) {
      setTeam(null);
      setMembers([]);
      setMyRole(null);
      setLoading(false);
      return;
    }

    setMyRole(membership.role as "owner" | "admin" | "member");

    const { data: teamData } = await supabase
      .from("teams")
      .select("*")
      .eq("id", membership.team_id)
      .single();

    if (teamData) {
      setTeam(teamData as unknown as Team);
    }

    const { data: membersData } = await supabase
      .from("team_members")
      .select("*, profiles(name, email)")
      .eq("team_id", membership.team_id)
      .order("joined_at", { ascending: true });

    setMembers((membersData as unknown as TeamMember[]) ?? []);

    // Fetch pending invitations if admin/owner
    if (["owner", "admin"].includes(membership.role)) {
      const { data: invData, error: invError } = await supabase
        .from("team_invitations")
        .select("*")
        .eq("team_id", membership.team_id)
        .eq("status", "pending")
        .order("created_at", { ascending: false });

      if (invError) {
        console.error("Error fetching invitations:", invError);
      }
      setInvitations((invData as unknown as TeamInvitation[]) ?? []);
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    fetchTeam();

    // Listen for realtime changes to team_members (e.g. being removed)
    const getChannel = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      return supabase
        .channel("team-membership-changes")
        .on(
          "postgres_changes",
          {
            event: "DELETE",
            schema: "public",
            table: "team_members",
          },
          (payload) => {
            // If the deleted record is for the current user, reset team state
            if (payload.old && (payload.old as any).user_id === user.id) {
              setTeam(null);
              setMembers([]);
              setMyRole(null);
              setInvitations([]);
            } else {
              // Another member was removed, just refresh the list
              fetchTeam();
            }
          }
        )
        .on(
          "postgres_changes",
          {
            event: "INSERT",
            schema: "public",
            table: "team_members",
          },
          () => {
            fetchTeam();
          }
        )
        .subscribe();
    };

    let channelRef: ReturnType<typeof supabase.channel> | null = null;
    getChannel().then((ch) => { channelRef = ch ?? null; });

    return () => {
      if (channelRef) {
        supabase.removeChannel(channelRef);
      }
    };
  }, [fetchTeam]);

  const removeMember = async (memberId: string) => {
    const { error } = await supabase
      .from("team_members")
      .delete()
      .eq("id", memberId);

    if (!error) {
      setMembers((prev) => prev.filter((m) => m.id !== memberId));
    }
    return { error };
  };

  const cancelInvitation = async (invitationId: string) => {
    const { error } = await supabase
      .from("team_invitations")
      .delete()
      .eq("id", invitationId);

    if (!error) {
      setInvitations((prev) => prev.filter((i) => i.id !== invitationId));
    }
    return { error };
  };

  const isOwner = myRole === "owner";
  const isAdmin = myRole === "owner" || myRole === "admin";

  return {
    team,
    members,
    invitations,
    myRole,
    loading,
    isOwner,
    isAdmin,
    refetch: fetchTeam,
    removeMember,
    cancelInvitation,
  };
}
